/**
 * List of known route paths that should not be treated as brand slugs
 */
const KNOWN_ROUTES = [
	'search-location',
	'my-location',
	'cart',
	'checkout',
	'login',
	'register',
	'my-account',
	'alerts',
	'offers',
	'explore',
	'my-addresses',
	'my-orders',
	'categories',
	'stores',
	'store',
	'clones',
	'fresh-meat',
	'new-test',
	'login/forgot-password',
	'running-order',
];

/**
 * Detect brand/restaurant unique slug from URL
 * Supports patterns like:
 * - /brand-slug (brand home)
 * - /brand-slug/stores/123 (store under brand)
 * - /brand-slug/cart (cart under brand)
 * - /brand-slug/... (all routes under brand)
 * - / (root - checks for single brand in localStorage)
 */
export const detectRestaurantSlug = () => {
	const pathname = window.location.pathname;
	
	// Split the pathname into segments
	const segments = pathname.split('/').filter(seg => seg);

	// If no segments, it's the home page (root)
	if (segments.length === 0) {
		// Check if there's a single brand for domain (highest priority)
		const singleBrand = localStorage.getItem('singleBrandForDomain');
		if (singleBrand) {
			return singleBrand;
		}
		// Fallback to last visited brand
		const lastVisitedBrand = localStorage.getItem('lastVisitedBrand');
		if (lastVisitedBrand) {
			return lastVisitedBrand;
		}
		return null;
	}

	// Check if first segment is a known route (not a brand slug)
	const firstSegment = segments[0];
	if (KNOWN_ROUTES.includes(firstSegment)) {
		// This is a known route, not a brand slug
		// Use single brand or last visited brand from localStorage
		const singleBrand = localStorage.getItem('singleBrandForDomain');
		if (singleBrand) {
			return singleBrand;
		}
		const lastVisitedBrand = localStorage.getItem('lastVisitedBrand');
		if (lastVisitedBrand) {
			return lastVisitedBrand;
		}
		return null;
	}

	// First segment is the brand/restaurant slug
	return firstSegment;
};

/**
 * Get the brand prefix from URL
 * Returns empty string if single brand exists for domain (no brand slug needed)
 * Falls back to URL-based brand or last visited brand
 */
export const getBrandPrefix = () => {
	// If single brand exists for domain, always return empty string (no brand slug in URLs)
	const singleBrand = localStorage.getItem('singleBrandForDomain');
	if (singleBrand) {
		return '';
	}
	
	const pathname = window.location.pathname;
	const segments = pathname.split('/').filter(seg => seg);
	
	// If at root (/), check for last visited brand
	if (segments.length === 0) {
		// Fallback to last visited brand for redirects
		return localStorage.getItem('lastVisitedBrand') || '';
	}
	
	// Check if first segment is a known route (not a brand slug)
	const firstSegment = segments[0];
	if (KNOWN_ROUTES.includes(firstSegment)) {
		// This is a known route, use last visited brand
		return localStorage.getItem('lastVisitedBrand') || '';
	}
	
	// First segment is the brand (for backward compatibility)
	return firstSegment;
};

/**
 * Store restaurant slug in localStorage
 */
export const storeRestaurantSlug = (slug) => {
	if (slug) {
		localStorage.setItem('restaurant_unique_slug', slug);
	}
};

/**
 * Get restaurant slug from localStorage
 */
export const getStoredRestaurantSlug = () => {
	return localStorage.getItem('restaurant_unique_slug');
};

/**
 * Clear stored restaurant slug
 */
export const clearRestaurantSlug = () => {
	localStorage.removeItem('restaurant_unique_slug');
};

/**
 * Check if current path is a restaurant-specific page
 */
export const isRestaurantSpecificPage = () => {
	const slug = detectRestaurantSlug();
	return slug !== null;
};

/**
 * Create a brand-scoped route
 * Example: brandRoute('/cart') => '/suguna-chicken/cart' or '/cart' (if at root with single brand)
 * Uses last visited brand if no brand in current URL
 * Returns root-level route if single brand exists for domain
 */
export const brandRoute = (path) => {
	const brandSlug = getBrandPrefix();
	
	// If empty string (at root with single brand), return path as-is
	if (brandSlug === '') {
		return path;
	}
	
	if (!brandSlug) {
		return path;
	}
	
	// Remove leading slash from path if it exists
	const cleanPath = path.startsWith('/') ? path.substring(1) : path;
	
	// If path is empty or just "/", return brand home
	if (!cleanPath || cleanPath === '') {
		return `/${brandSlug}`;
	}
	
	return `/${brandSlug}/${cleanPath}`;
};

/**
 * Get the base brand URL
 * Example: '/suguna-chicken' or '/' (if at root with single brand)
 */
export const getBrandBaseUrl = () => {
	const brandSlug = getBrandPrefix();
	// If empty string (at root with single brand), return root
	if (brandSlug === '') {
		return '/';
	}
	return brandSlug ? `/${brandSlug}` : '/';
};

